/**
 * Comprensión: representación estructurada de lo que pide el usuario.
 *
 * Un `Understander` convierte un mensaje (más un contexto mínimo) en una `Understanding`:
 * una intención tipada y una confianza entre 0 y 1. Hoy solo existe el determinista
 * (understand.ts). Un modelo de lenguaje futuro implementaría la misma interfaz y su salida
 * pasaría SIEMPRE por `validateUnderstanding`: lo que no cumple el esquema se trata como
 * no entendido. La comprensión nunca modifica datos ni decide ejecutar nada: eso lo hace
 * la conversación, y solo tras una confirmación explícita.
 */
import { z } from 'zod';
import { interpretationSchema } from './interpreter';

const localDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
const day = z.number().int().min(1).max(7);

/** Cambios concretos que aparecen en un mensaje ("a las 19", "45 minutos", "los martes"). */
export const changesSchema = z.object({
  /** null = "sin hora". */
  time: hhmm.nullable().optional(),
  /** Hora ambigua ("a las 3"): se pregunta cuál de las dos. */
  timeOptions: z.tuple([hhmm, hhmm]).optional(),
  slot: z.enum(['morning', 'afternoon', 'evening']).optional(),
  date: localDate.optional(),
  minutes: z.number().int().min(1).max(600).optional(),
  days: z.array(day).min(1).max(7).optional(),
  /** Días excluidos ("excepto los miércoles"), para explicarlo. */
  except: z.array(day).max(6).optional(),
  /** Días añadidos ("y el sábado también") en lugar de sustituidos. */
  addDays: z.boolean().optional(),
  quota: z.number().int().min(1).max(6).optional(),
  /** Cantidad semanal ("seis horas por semana"): se reparte en sesiones. */
  weeklyMinutes: z.number().int().min(15).max(3000).optional(),
  /** Días que deben quedar libres ("déjame libre el domingo"). */
  freeDays: z.array(day).min(1).max(6).optional(),
  name: z.string().trim().min(1).max(80).optional(),
  /** "Cambiemos el horario" sin decir cuál: hay que preguntar. */
  askTime: z.boolean().optional(),
  invalidTime: z.boolean().optional(),
  invalidDate: z.boolean().optional(),
});
export type Changes = z.infer<typeof changesSchema>;

/** Una preferencia que se propone recordar (ver preferences.ts). */
export const preferenceChangeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('earliest'), time: hhmm }),
  z.object({ kind: z.literal('latest'), time: hhmm }),
  z.object({ kind: z.literal('slot'), activity: z.string().trim().min(1).max(80), slot: z.enum(['morning', 'afternoon', 'evening']) }),
  z.object({ kind: z.literal('freeDays'), days: z.array(day).min(1).max(6) }),
]);
export type PreferenceChange = z.infer<typeof preferenceChangeSchema>;

export const windowSchema = z.object({ days: z.array(day).min(1).max(7), start: hhmm, end: hhmm });
export type Window = z.infer<typeof windowSchema>;

export const QUERY_KINDS = ['query_habits', 'query_stats', 'query_tasks', 'query_agenda', 'query_focus', 'query_notes', 'query_journal'] as const;

export const intentSchema = z.discriminatedUnion('type', [
  /** Orden puntual resuelta por el intérprete de órdenes (crear, registrar, listas…). */
  z.object({ type: z.literal('command'), interpretation: interpretationSchema }),
  /** Objetivo a convertir en hábito planificado ("estudiar 30 min al día salvo los miércoles"). */
  z.object({
    type: z.literal('plan_goal'),
    activity: z.string().trim().max(80).nullable(),
    changes: changesSchema,
    /** Crear un hábito nuevo aunque exista uno de la misma actividad (elegido por el usuario). */
    forceNew: z.boolean().optional(),
  }),
  /** Organizar la semana; `windows` si el mensaje ya trae la disponibilidad. */
  z.object({ type: z.literal('plan_week'), windows: z.array(windowSchema).max(14).nullable() }),
  /** Rutina con varias actividades ("organízame la semana para estudiar, entrenar y descansar"). */
  z.object({
    type: z.literal('plan_routine'),
    activities: z.array(z.object({ name: z.string().trim().min(1).max(80), changes: changesSchema })).min(1).max(6),
    /** Días de descanso pedidos ("descansar", "libre el domingo"). */
    freeDays: z.array(day).max(6),
    windows: z.array(windowSchema).max(14).nullable(),
  }),
  /** Dejar días libres sin una propuesta abierta: mover lo que haya esos días. */
  z.object({ type: z.literal('free_day'), days: z.array(day).min(1).max(6) }),
  /** Pasar un hábito o tarea de un día de la semana a otro ("pasa el entrenamiento del miércoles al jueves"). */
  z.object({ type: z.literal('move_day'), targetText: z.string().max(500), from: day, to: day }),
  /** Otra versión de la propuesta en curso. */
  z.object({ type: z.literal('alternative') }),
  /** Deshacer el último cambio guardado desde el chat. */
  z.object({ type: z.literal('undo_last') }),
  /** Recordar una preferencia ("prefiero entrenar por la mañana"), tras confirmarla. */
  z.object({ type: z.literal('remember_pref'), change: preferenceChangeSchema }),
  /** "¿Qué sabes de mí?": las preferencias guardadas. */
  z.object({ type: z.literal('query_prefs') }),
  z.object({ type: z.literal('query_habits') }),
  z.object({ type: z.literal('query_stats'), scope: z.enum(['neglected', 'summary', 'habit']), habitText: z.string().max(500).nullable() }),
  z.object({ type: z.literal('query_tasks') }),
  z.object({ type: z.literal('query_agenda'), from: localDate, to: localDate }),
  z.object({ type: z.literal('query_focus') }),
  z.object({ type: z.literal('query_notes'), query: z.string().trim().max(80) }),
  z.object({ type: z.literal('query_journal'), date: localDate.nullable() }),
  /** Editar un hábito existente: meta, días, nombre u horario del recordatorio. */
  z.object({ type: z.literal('edit_habit'), habitText: z.string().max(500).nullable(), changes: changesSchema }),
  z.object({ type: z.literal('reschedule_task'), taskText: z.string().max(500).nullable(), changes: changesSchema }),
  z.object({ type: z.literal('log_focus'), minutes: z.number().int().min(1).max(600).nullable(), habitText: z.string().max(500).nullable(), date: localDate.nullable() }),
  /** Cambiar la propuesta en curso. */
  z.object({ type: z.literal('modify'), changes: changesSchema, text: z.string().max(500) }),
  /** Quitar un elemento de la propuesta en curso ("quita la tarea X"). */
  z.object({ type: z.literal('remove_item'), text: z.string().max(500) }),
  /** `explicit`: se escribió el verbo de la acción ("eliminar"), exigido para las destructivas. */
  z.object({ type: z.literal('confirm'), explicit: z.boolean() }),
  z.object({ type: z.literal('cancel') }),
  z.object({ type: z.literal('reset') }),
  z.object({ type: z.literal('greeting') }),
  z.object({ type: z.literal('help') }),
  z.object({ type: z.literal('thanks') }),
  /** No se entendió con confianza suficiente; `candidates` son interpretaciones posibles. */
  z.object({ type: z.literal('unknown'), candidates: z.array(z.string()).max(3) }),
]);
export type Intent = z.infer<typeof intentSchema>;
export type IntentType = Intent['type'];

export const understandingSchema = z.object({
  intent: intentSchema,
  confidence: z.number().min(0).max(1),
  /** Quién lo interpretó: se indica en la conversación cuando fue la IA. */
  via: z.enum(['rules', 'ai']).optional(),
});
export type Understanding = z.infer<typeof understandingSchema>;

/** Por debajo de este umbral la conversación pregunta en lugar de actuar. */
export const MIN_CONFIDENCE = 0.55;

export interface UnderstandContext {
  today: string;
  now: number;
  tz: string;
  locale: 'es' | 'en';
  /** Día de inicio de semana ISO (1 = lunes). */
  weekStart: number;
  habits: { id: string; name: string; kind: 'build' | 'quit' | 'task'; goalType: 'binary' | 'numeric'; unit: string | null; defaultIncrement: number; archived: boolean; completed: boolean }[];
  lists: { id: string; title: string }[];
  /** Hay una propuesta o pregunta abierta: "sí", "a las 7" o "cancela" se refieren a ella. */
  pending: 'proposal' | 'question' | null;
}

export interface Understander {
  readonly id: string;
  understand(text: string, ctx: UnderstandContext): Promise<Understanding>;
}

/** Valida la salida de cualquier comprensión; lo inválido cuenta como no entendido. */
export function validateUnderstanding(raw: unknown): Understanding {
  const r = understandingSchema.safeParse(raw);
  return r.success ? r.data : { intent: { type: 'unknown', candidates: [] }, confidence: 0 };
}
